import React, { PropTypes } from 'react';
import { connect } from 'react-redux';

import { googlePlaceShape } from '../../proptypes';

import Button from '../Button';
import Geopicker from '../Geopicker/Geopicker.react';
import Placepicker from '../Placepicker/Placepicker.react';
import PlacepickerPreset from '../PlacepickerPreset';
import PlacepickerPresetCountry from '../PlacepickerPreset/components/PlacepickerPresetCountry';
import Underscore from '../Underscore';

import { srOnly } from '../../base-styles/mixins.module.css';

import {
  actions,
  arrow,
  arrowField,
  button,
  checkbox,
  checkboxActive,
  container,
  conversational as convoStyle,
  edit,
  findEvents,
  genericField,
  geopicker,
  input,
  inputEditing,
  label as labelStyle,
  locationField,
  locationUnderscore,
  maskButton,
  maskVisual,
  option,
  options,
  optionActive,
  outerContainer,
  placeContainer,
  placepicker,
  preset,
  radius,
  radiusContainer,
  section,
  selection,
  sportField,
  sportUnderscore,
  sportDistanceField,
  suggested,
  underscore,
  value,
} from './styles.module.css';

/**
 * Homepage search presentational component.
 */
const HomepageSearch = ({
  className,
  countryCode,
  distances,
  editing,
  fetching,
  onChange,
  onClose,
  onEdit,
  onFetched,
  onFetching,
  onSubmit,
  sports,
  values,
}) => (
  <div>
    <div className={outerContainer}>
      <form id="filters" className={[container, className].join(' ')}>
        {editing && <div className={maskVisual} />}
        {editing && <button type="button" className={maskButton} onClick={onClose} />}
        <div className={section}>
          <span className={convoStyle}>I am </span>
          <Underscore className={sportUnderscore} slim>
            <div className={sportField}>
              <div className={value}>
                {values.sport
                  // Find the sport object for the given selection and return the label
                  ? <span className={selection}>
                    {sports.filter(s => s.id === values.sport)[0].noun}</span>
                  // Or just show default string, i.e. none specified
                  : 'Sport'
                }
                <div className={arrowField} />
                <button type="button" name="sport" onClick={onEdit} className={edit}>
                  <span className={srOnly}>{'Edit'}</span>
                </button>
              </div>
              {editing === 'sport' &&
                <div className={inputEditing}>
                  <span className={labelStyle}>Sport</span>
                  <div className={options}>
                    {sports.map(({ id, noun }) => (
                      <label
                        key={id}
                        htmlFor={`sport-${id}`}
                        className={values.sport === id ? optionActive : option}
                      >
                        <input
                          type="radio"
                          id={`sport-${id}`}
                          name="sport"
                          onChange={onChange}
                          checked={values.sport ? values.sport === id : false}
                          value={id}
                        /> {noun}
                      </label>))}
                  </div>
                </div>}
            </div>
          </Underscore>
        </div>
        <div className={section}>
          <span className={convoStyle}>looking for </span>
          <Underscore className={underscore} slim>
            {!distances &&
              <div className={sportDistanceField}>
                <div className={value}>
                  <span>{'All Distances'}</span>
                </div>
              </div>}
            {distances && <div className={sportDistanceField}>
              <div className={value}>
                {(values.sportDistance && values.sportDistance.length)
                  ? <span className={selection}>
                    {values.sportDistance.map(selectedId =>
                      // For each selection, find the matching distance object and return the label
                      distances.filter(d => selectedId === d.id)[0].label)
                      // Join the first two and show more count e.g. '+2',
                      // given length of selection array
                      .slice(0, 2).join(', ') + (values.sportDistance.length > 2 ? `, +${values.sportDistance.length - 2} more` : '')}</span>
                    // Or just show default string, i.e. none specified
                  : <span>{'All Distances'}</span>
                }
                <div className={arrowField} />
                {distances &&
                  <button type="button" name="sportDistance" onClick={onEdit} className={edit}>
                    <span className={srOnly}>{'Edit'}</span>
                  </button>}
              </div>
              {(distances && editing === 'sportDistance') &&
                <div className={inputEditing}>
                  <span className={labelStyle}>{'Distance'}</span>
                  {distances &&
                    <div className={options}>
                      {distances.map(({ id, label }) => {
                        const name = `sportDistance-${id}`;
                        return (
                          <label
                            key={name}
                            htmlFor={`sportDistance-${id}`}
                            className={values.sportDistance &&
                              values.sportDistance.includes(id) ? checkboxActive : checkbox}
                          >
                            <input
                              type="checkbox"
                              id={name}
                              name={name}
                              onChange={onChange}
                              checked={values.sportDistance ?
                                values.sportDistance.includes(id) : false}
                            /> {label}
                          </label>
                        );
                      })}
                    </div>}
                  <div className={actions}>
                    <Button type="button" onClick={onClose} className={button}>{'Done'}</Button>
                  </div>
                </div>}
            </div>}
          </Underscore>
        </div>
        <div className={section}>
          <span className={convoStyle}>events near </span>
          <Underscore className={locationUnderscore} slim>
            <div className={locationField}>
              <div className={value}>
                {values.location
                  ? <span className={selection}>{values.location.name}</span>
                  : 'Anywhere'}
                <div className={arrowField} />
                <button type="button" name="location" onClick={onEdit} className={edit}>
                  <span className={srOnly}>{'Edit'}</span>
                </button>
              </div>
              {editing === 'location' &&
                <div className={inputEditing}>
                  <label htmlFor="location" className={labelStyle}>{'Location'}</label>
                  <div className={[genericField, options].join(' ')}>
                    <div className={placeContainer}>
                      <Placepicker
                        id="location"
                        name="location"
                        onChange={onChange}
                        className={placepicker}
                        countryCode={countryCode}
                        value={values.location || null}
                      />
                    </div>
                    {values.location
                      && !['country', 'administrative_area_level_1', 'administrative_area_level_2', 'bounds'].includes(values.location.types[0]) &&
                      <div className={radiusContainer}>
                        <div className={arrow} />
                        <select
                          id="locationSearchRadius"
                          name="locationSearchRadius"
                          className={radius}
                          onChange={onChange}
                          value={values.locationSearchRadius || ''}
                        >
                          <option value="">- Choose Radius -</option>
                          <option value="10">Within 10 km</option>
                          <option value="25">Within 25 km</option>
                          <option value="50">Within 50 km</option>
                          <option value="100">Within 100 km</option>
                          <option value="200">Within 200 km</option>
                        </select>
                      </div>}
                    <Geopicker
                      id="geolocation"
                      name="geolocation"
                      label="Current Location"
                      className={geopicker}
                      checked={values.geolocation || false}
                      onChange={onChange}
                      onFetched={onFetched}
                      onFetching={onFetching}
                    />
                  </div>

                  <div className={actions}>
                    <Button type="button" onClick={onClose} disabled={fetching} className={button}>{'Done'}</Button>
                  </div>
                </div>}
            </div>
          </Underscore>
        </div>
        <div className={findEvents}>
          <Button primary onClick={onSubmit}>Find Events</Button>
        </div>
      </form>
    </div>
  </div>
);

HomepageSearch.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Array of distances to render as checkbox inputs.
   */
  distances: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  /**
   * String name of field currently being edited.
   */
  editing: PropTypes.oneOf(['', 'sport', 'sportDistance', 'date', 'location']),
  /**
   * Bool describing state of required data fetch.
   */
  fetching: PropTypes.bool,
  /**
   * Function called on any field change.
   */
  onChange: PropTypes.func,
  /**
   * Function called when clicking outside edited area or 'done' button.
   */
  onClose: PropTypes.func,
  /**
   * Function called on field edit.
   */
  onEdit: PropTypes.func,
  /**
   * Function called on fetching required data, e.g. Geolocation.
   */
  onFetched: PropTypes.func,
  /**
   * Function called on response or error of required data, e.g. Geolocation.
   */
  onFetching: PropTypes.func,
  /**
   * Function called on form submit.
   */
  onSubmit: PropTypes.func,
  /**
   * Array of sports to render in select input.
   */
  sports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  /**
   * Object representing current form selections.
   */
  values: PropTypes.shape({
    geolocation: PropTypes.bool,
    location: googlePlaceShape,
    locationSearchRadius: PropTypes.number,
    sport: PropTypes.number,
    sportDistance: PropTypes.arrayOf(
      PropTypes.number.isRequired,
    ),
  }),
  countryCode: PropTypes.string,
};

HomepageSearch.defaultProps = {
  className: '',
  distances: undefined,
  editing: undefined,
  fetching: false,
  onChange: () => {},
  onClose: () => {},
  onEdit: () => {},
  onFetched: () => {},
  onFetching: () => {},
  onSubmit: () => {},
  values: {},
  countryCode: '',
};


const mapStateToProps = state => ({
  countryCode: state.countryCode,
});

// Wrap in [react-redux connect](https://github.com/reactjs/react-redux/blob/master/docs/api.md#connect)
const HomepageSearchWithRedux = connect(mapStateToProps)(HomepageSearch); // eslint-disable-line no-class-assign, max-len

export default HomepageSearchWithRedux;



// WEBPACK FOOTER //
// ./app/src/components/HomepageSearch/index.jsx